<template>
  <div class="tax-content-container text-xs-left">
    <template v-if="taxDetails.length > 0">
      <div class="summary-header">
        <sc-h1>
          Tax Summary
        </sc-h1>
      </div>

      <v-container grid-list-lg>
        <v-layout row wrap class="cards-row">
          <v-flex
            md6
            sm12
            v-for="(detail, cardIndex) in taxDetails"
            :key="cardIndex"
            class="card"
          >
            <div class="tax-details-card">
              <v-layout class="card-title-container">
                <sc-h4>{{ detail.title }}</sc-h4>
              </v-layout>

              <v-divider class="my-2"></v-divider>

              <div
                v-for="(row, rowIndex) in detail.rows"
                :key="rowIndex"
                class="detail-row form-control"
                v-if="rowOptions[row.label]"
              >
                <v-layout class="px-2 py-1">
                  <div style="flex: auto;">
                    <span :class="rowOptions[row.label].cssClass">{{
                      row.label
                    }}</span>
                  </div>

                  <span
                    class="readonly-field"
                    :class="rowOptions[row.label].cssClass"
                  >
                    {{ getTrueValue(detail.title, row) }}
                  </span>
                </v-layout>

                <div
                  v-if="
                    row.value !== 0 &&
                      getDisclaimerText(detail.title, row.label)
                  "
                  class="disclaimer-container"
                >
                  {{ getDisclaimerText(detail.title, row.label) }}
                  <span class="amount-caret"></span>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </template>

    <template v-if="Object.keys(paymentVouchersGroupedById).length > 0">
      <div class="mt-5 summary-header">
        <sc-h1>
          Payments
        </sc-h1>
      </div>

      <v-container grid-list-lg>
        <v-layout row wrap class="cards-row">
          <v-flex
            md6
            sm12
            v-for="(vouchers, voucherId) in paymentVouchersGroupedById"
            :key="voucherId"
            class="card"
          >
            <div class="payment-voucher-card">
              <sc-h4>{{ getFullPaymentName(vouchers[0]) }}</sc-h4>
              <span class="body-1 grey--text">{{
                getVoucherCode(vouchers[0])
              }}</span>
              <v-divider class="my-2"></v-divider>

              <div class="voucher-table">
                <div class="voucher-table-header detail-row">
                  <span>Due Date</span>
                  <span>Amount</span>
                </div>

                <div
                  v-for="(voucher, vIndex) in vouchers"
                  :key="`${voucherId}-${vIndex}`"
                  class="voucher-table-row detail-row"
                >
                  <span>
                    {{
                      voucher.is_due_asap
                        ? 'Due ASAP'
                        : voucher.due_date || 'N/A'
                    }}
                    {{
                      voucher.quarter && voucher.quarter !== 'None'
                        ? ` (${voucher.quarter})`
                        : ''
                    }}
                  </span>
                  <span
                    >{{ formatCurrency(voucher.amount) }}
                    <span class="paid" v-if="voucher.is_paid">
                      Paid
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import TaxDetailMixin from '@/mixins/TaxDetailMixin';

export default {
  mixins: [TaxDetailMixin],

  props: {
    taxReturn: {
      type: Object,
    },
  },

  data() {
    return {
      voucherDefinitions: [],
    };
  },

  computed: {
    taxDetails() {
      let taxDetails = this.taxReturn.tax_details.slice();

      if (this.taxReturn.paper_filed_returns) {
        this.taxReturn.paper_filed_returns.forEach(paperReturn => {
          if (paperReturn.tax_details && paperReturn.tax_details.length > 0) {
            taxDetails = taxDetails.concat(paperReturn.tax_details);
          }
        });
      }

      return taxDetails;
    },

    // This value is required for TaxDetailMixin
    currentTaxDetails() {
      return this.taxDetails;
    },

    paymentVouchersGroupedById() {
      if (
        !this.taxReturn ||
        !this.taxReturn.payment_vouchers ||
        this.taxReturn.payment_vouchers.length === 0
      ) {
        return {};
      }
      return this.taxReturn.payment_vouchers.reduce((acc, voucher) => {
        let key;
        if (voucher.voucher_id === 'other') {
          key = voucher.custom_payment_name || 'Other';
        } else {
          key = voucher.voucher_id || 'Unnamed Voucher';
        }

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(voucher);

        return acc;
      }, {});
    },
  },

  methods: {
    formatCurrency(numValue) {
      const num = Number(numValue);
      if (isNaN(num)) {
        return '$0';
      }
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    formatPercentage(numValue) {
      const num = Number(numValue);
      if (isNaN(num)) {
        return '0%';
      }
      return `${Math.round(num)}%`;
    },
    getFullPaymentName(paymentVoucher) {
      let paymentName =
        paymentVoucher.voucher_id === 'other'
          ? paymentVoucher.custom_payment_name
          : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
          ? `${
              this.getVoucherDefinitionById(paymentVoucher.voucher_id).state
            } ${this.getVoucherDefinitionById(paymentVoucher.voucher_id).name}`
          : 'Unknown';

      return paymentName;
    },
    getVoucherCode(paymentVoucher) {
      return paymentVoucher.voucher_id === 'other'
        ? ''
        : this.getVoucherDefinitionById(paymentVoucher.voucher_id)
        ? this.getVoucherDefinitionById(paymentVoucher.voucher_id).code
        : '';
    },
    getVoucherDefinitionById(id) {
      return this.voucherDefinitions.find(voucher => voucher.id === id);
    },
    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.voucherDefinitions = response.data;
    },
  },

  async mounted() {
    await this.getVoucherDefinitions();
  },
};
</script>

<style scoped>
.tax-content-container {
  max-width: 1000px;
  width: 100%;
  margin: 32px auto;
  padding: 16px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-header h1 {
}

.cards-row {
  margin-bottom: 20px;
}

.card-title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
  height: 40px;
}

.tax-details-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  flex-grow: 1;
}

.detail-row {
  padding: 8px 0;
  font-size: 15px;
}

.amount-refunded {
  color: var(--green-700);
  font-weight: bold;
}

.amount-owed {
  color: var(--red-700);
  font-weight: bold;
}

.payment-voucher-card {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  flex-grow: 1;
}

.payment-voucher-card .voucher-item-details {
  margin-bottom: 10px;
}

.payment-voucher-card .voucher-item-details:last-child {
  margin-bottom: 0;
}

.voucher-table {
  width: 100%;
}

.voucher-table-header {
  font-weight: bold;
  border-bottom: 1px solid #e0e0e0;
}

.voucher-table-row span:first-child,
.voucher-table-header span:first-child {
  text-align: left;
  flex-basis: 60%; /* Give more space to date/quarter */
}

.voucher-table-row span:last-child,
.voucher-table-header span:last-child {
  text-align: right;
  flex-basis: 40%;
}

.paid {
  color: #fff;
  background-color: var(--green-700);
  border-radius: 5px;
  padding: 4px 8px;
  font-size: 13px;
}

.detail-row {
  margin-bottom: 8px;
}

.voucher-table .detail-row {
  display: flex;
  justify-content: space-between;
}

.disclaimer-container {
  background-color: var(--gray-100);
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  color: #555555;
  position: relative;
  border: 1px solid #ddd;
}

.amount-caret {
  position: absolute;
  display: inline-block;
  top: -6px;
  right: 8px;
  width: 0;
  height: 0;
  border-bottom: 6px solid var(--gray-100);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  margin-left: 8px;
  vertical-align: middle;
  filter: drop-shadow(0 -1px 0 #ddd) drop-shadow(-1px 0 0 #ddd)
    drop-shadow(1px 0 0 #ddd);
}
</style>
